import { Filter } from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useMe,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import { parseAsString, useQueryStates } from '@vrplatform/query-params';
import { type Maybe, day, ensure, sortBy, utc } from '@vrplatform/utils';
import { Group } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useTaxStatementStatusOptions } from './useTaxStatementStatusOptions';

export const STORYBOOK_TAX_STATEMENT_LIST_YEAR_OPTIONS_QUERY_KEY =
  'tax-statement-list-year-options';

export const useTaxStatementListQueryFilter = () => {
  const [filter, setFilter] = useQueryStates({
    search: parseAsString,
    year: parseAsString,
    status: parseAsString,
  });

  return {
    filter,
    setFilter,
    year: Number(filter?.year)
      ? day().set('year', Number(filter.year)).year()
      : day().year(),
    resetFilter: () =>
      setFilter((e) => ({
        year: e.year,
        search: null,
      })),
  };
};

export const TaxStatementListFilter = () => {
  const { filter, setFilter } = useTaxStatementListQueryFilter();

  return (
    <Group gap="xs">
      <Filter.Search
        value={filter?.search || ''}
        setValue={(v) => setFilter({ search: v || null })}
      />
      <TaxStatementsYearFilter />
      <StatusFilter />
    </Group>
  );
};

export const TaxStatementsYearFilter = () => {
  const { id: meId } = useMe();
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = useTaxStatementListQueryFilter();
  const [dashboard] = useDashboard();

  const { data: months, isLoading: loading } = useQuery(
    (q, args) => {
      return q
        .ownerStatements({
          where: args.where,
          distinct_on: ['startAt'],
          order_by: [{ startAt: 'desc' }],
        })
        .map((statement) => ({
          id: statement.id,
          startAt: statement.startAt || '',
        }));
    },
    {
      queryKey: ['ownerStatements'],
      storybookQueryKey: STORYBOOK_TAX_STATEMENT_LIST_YEAR_OPTIONS_QUERY_KEY,
      variables: {
        where: ensure<gqlV2.owner_statement_bool_exp>({
          tenantId: { _eq: teamId },

          owners:
            dashboard === 'owner'
              ? {
                  newOwner: {
                    userAccesses: {
                      userId: { _eq: meId },
                    },
                  },
                }
              : {
                  newOwnerId: {
                    _is_null: false,
                  },
                },
        }),
      },
    }
  );

  const availableMonths = useMemo<SelectItem[]>(() => {
    const sortByMonth = sortBy(months || [], (x) => x.startAt);

    return sortByMonth
      .map((i: { id: string; startAt: string }) => ({
        label: utc(i.startAt).format('YYYY'),
        value: utc(i.startAt).format('YYYY'),
      }))
      .filter(
        (i, index, self) => self.findIndex((t) => t.value === i.value) === index
      )
      .reverse();
  }, [months]);

  const value = availableMonths.find((i) => i.value === filter?.year) || null;

  const previousYear = day().subtract(1, 'year').format('YYYY');

  const defaultYear =
    availableMonths.find((item) => item.value === previousYear)?.value ||
    availableMonths[0]?.value;

  useEffect(() => {
    if (!value && defaultYear)
      setFilter({ year: defaultYear }, { history: 'replace' });
  }, [defaultYear, value]);

  return (
    <Filter.Select
      value={value}
      setValue={(v) => v?.value && setFilter({ year: v?.value ?? null })}
      label="Year"
      type="single"
      withinPortal
      data={{
        options: availableMonths,
        loading,
      }}
      hideClearButton
    />
  );
};

const StatusFilter = () => {
  const {
    setFilter,
    filter: { status },
  } = useTaxStatementListQueryFilter();

  return (
    <TaxStatementStatusFilter
      setValue={(v) =>
        setFilter({
          status: v ?? null,
        })
      }
      value={status}
    />
  );
};

export const TaxStatementStatusFilter = ({
  setValue,
  value: current,
}: {
  value: Maybe<string>;
  setValue: (v: string | null) => void;
}) => {
  const opts = useTaxStatementStatusOptions('draft', 'single');
  const options = useMemo<SelectItem<gqlV2.tax_statement_status_enum>[]>(
    () =>
      opts.map((x) => ({
        ...x,
        disabled: false,
      })),
    [opts]
  );

  const value = options.find((i) => i.value === current) || null;

  return (
    <Filter.Select
      value={value ? { ...value, icon: undefined } : null}
      setValue={(v) => setValue(v?.value ?? null)}
      label="Status"
      type="single"
      withinPortal
      popoverWidth={340}
      data={{
        options: options,
      }}
    />
  );
};
